import {
  useEffect,
  useRef,
  useState,
} from "react";
import { apiRequest, peekApiResponse } from "../../../../shared/api/client";
import {
  type SessionUser,
} from "../../../../shared/auth/access";
import { createRequestWorkspaceService } from "../../application/queries/request-workspace";
import {
  asProcurementRole,
  type RequestWorkspaceDetail,
} from "../../domain/models";
import {
  procurementWorkspaceTarget,
  type ProcurementWorkspaceTarget,
} from "../../domain/selectors";
import { procurementQueryGateway } from "../../infrastructure/api/procurement-query-gateway";
import {
  compareProcurementWorkQueue,
  procurementStageOf,
  type PurchaseRequest,
} from "../../model";
import {
  defaultRequestStageFilterId,
  requestStageFilterOptions,
} from "../shared/request-stage-filters";

const requestWorkspaceService = createRequestWorkspaceService(
  procurementQueryGateway,
);

function defaultRequestDepartment(user: SessionUser) {
  if (user.role === "SATIN_ALMA" || user.dataScope !== "all") return user.department;
  return "Planlama";
}

function emptyRequestForm(user: SessionUser) {
  return {
    title: "",
    description: "",
    category: "",
    quantity: "1",
    unit: "adet",
    priority: "NORMAL",
    department: defaultRequestDepartment(user),
    due: "",
  };
}

export type RequestsModuleProps = {
  notify: (message: string) => void;
  composerOpen: boolean;
  setComposerOpen: (open: boolean) => void;
  user: SessionUser;
  initialRequestNo?: string;
  navigationVersion: number;
  dataRefreshVersion: number;
  onNestedBackChange: (handler: (() => void) | null) => void;
  onOpenRequestWorkspace: (
    requestNo: string,
    target: ProcurementWorkspaceTarget,
  ) => void;
};

export function useRequestsWorkspace({
  notify,
  composerOpen,
  setComposerOpen,
  user,
  initialRequestNo,
  navigationVersion,
  dataRefreshVersion,
  onNestedBackChange,
  onOpenRequestWorkspace,
}: RequestsModuleProps) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(() =>
    defaultRequestStageFilterId(user.role),
  );
  const [departmentFilter, setDepartmentFilter] = useState("Tümü");
  const [rows, setRows] = useState<PurchaseRequest[]>(
    () => peekApiResponse<PurchaseRequest[]>("/api/requests") ?? [],
  );
  const [form, setForm] = useState(() => emptyRequestForm(user));
  const [planningComposerStep, setPlanningComposerStep] = useState<
    "suggestion" | "form"
  >("suggestion");
  const [loading, setLoading] = useState(
    () => !peekApiResponse<PurchaseRequest[]>("/api/requests"),
  );
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [detailRequestNo, setDetailRequestNo] = useState<string | null>(null);
  const requestModalOpenerRef = useRef<HTMLElement | null>(null);
  const createBusy = useRef(false);
  const createScope = useRef({ id: user.id, role: user.role });
  const createAttempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const listVersion = useRef(0);
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [requestDetail, setRequestDetail] =
    useState<RequestWorkspaceDetail | null>(null);
  const [requestDetailLoading, setRequestDetailLoading] = useState(false);
  const [requestDetailError, setRequestDetailError] = useState("");
  const stageOptions = requestStageFilterOptions(user.role);
  const selectedStageOption = stageOptions.find(
    (option) => option.id === statusFilter,
  );
  const departments = [...new Set(rows.map((request) => request.department))];
  const showDepartmentFilter =
    user.dataScope === "all" && departments.length > 1;
  useEffect(() => {
    const changed = createScope.current.id !== user.id ||
      createScope.current.role !== user.role;
    createScope.current = { id: user.id, role: user.role };
    if (changed) createAttempt.current = null;
    setForm((current) => changed
      ? emptyRequestForm(user)
      : { ...current, department: defaultRequestDepartment(user) });
    setStatusFilter(defaultRequestStageFilterId(user.role));
    setDepartmentFilter("Tümü");
    setDetailRequestNo(null);
    setRequestDetail(null);
    setRequestDetailError("");
  }, [user.id, user.role, user.dataScope, user.department]);
  useEffect(() => {
    if (composerOpen && user.role === "PLANLAMA") {
      setPlanningComposerStep("suggestion");
    }
  }, [composerOpen, user.role]);
  useEffect(() => {
    if (initialRequestNo) setExpandedRequest(initialRequestNo);
  }, [initialRequestNo, navigationVersion]);
  useEffect(() => {
    if (!expandedRequest && !detailRequestNo) {
      onNestedBackChange(null);
      return;
    }
    onNestedBackChange(() => {
      if (detailRequestNo) {
        closeRequestDetail();
        return;
      }
      setExpandedRequest(null);
    });
    return () => onNestedBackChange(null);
  }, [detailRequestNo, expandedRequest, onNestedBackChange]);
  useEffect(() => {
    if (!detailRequestNo || user.role !== "SATIN_ALMA") return;
    const app = document.querySelector<HTMLElement>(".app-shell");
    document.body.classList.add("procurement-modal-open");
    app?.setAttribute("inert", "");
    app?.setAttribute("aria-hidden", "true");
    requestAnimationFrame(() =>
      document
        .querySelector<HTMLElement>(
          ".specialist-request-workspace .manager-dossier-close",
        )
        ?.focus(),
    );
    return () => {
      document.body.classList.remove("procurement-modal-open");
      app?.removeAttribute("inert");
      app?.removeAttribute("aria-hidden");
      requestModalOpenerRef.current?.focus();
    };
  }, [detailRequestNo, user.role]);
  useEffect(() => {
    let active = true;
    const requestVersion = listVersion.current;
    const cachedRows = peekApiResponse<PurchaseRequest[]>("/api/requests");
    if (cachedRows) setRows(cachedRows);
    setLoading(!cachedRows);
    setLoadError("");
    apiRequest<PurchaseRequest[]>("/api/requests")
      .then((data) => {
        if (!active || requestVersion !== listVersion.current) return;
        setRows(data);
        setStatusFilter(
          (current) => current ?? defaultRequestStageFilterId(user.role),
        );
        setExpandedRequest(
          initialRequestNo &&
            data.some((request) => request.id === initialRequestNo)
            ? initialRequestNo
            : null,
        );
      })
      .catch((error) => {
        const message =
          error instanceof Error
            ? error.message
            : "Talepler sunucudan alınamadı.";
        if (!cachedRows) {
          if (active) setLoadError(message);
          notify(message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    dataRefreshVersion,
    initialRequestNo,
    navigationVersion,
    notify,
    reloadKey,
    user.id,
  ]);
  const workQueueToday = new Date();
  const visibleRows = rows
    .filter(
      (request) =>
        `${request.id} ${request.title} ${request.department}`
          .toLocaleLowerCase("tr")
          .includes(query.toLocaleLowerCase("tr")) &&
        (!selectedStageOption || selectedStageOption.matches(request)) &&
        (departmentFilter === "Tümü" ||
          request.department === departmentFilter),
    )
    .sort((left, right) =>
      compareProcurementWorkQueue(left, right, workQueueToday),
    );
  const createRequest = async () => {
    if (createBusy.current) return;
    if (!form.title.trim() || !form.category.trim() || !form.due.trim()) {
      notify("Ürün, kategori ve ihtiyaç tarihi zorunludur.");
      return;
    }
    if (user.role === "SATIN_ALMA" && !form.description.trim()) {
      notify("İhtiyacın açıklamasını girin.");
      return;
    }
    const quantity = Number(form.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0 || !form.unit.trim()) {
      notify("Geçerli bir miktar ve birim girin.");
      return;
    }
    const submittedScope = createScope.current;
    const body = JSON.stringify({ ...form, quantity });
    if (createAttempt.current?.fingerprint !== body) {
      createAttempt.current = { fingerprint: body, key: crypto.randomUUID() };
    }
    createBusy.current = true;
    setCreatingRequest(true);
    try {
      const nextRequest = await apiRequest<PurchaseRequest>("/api/requests", {
        method: "POST",
        headers: { "Idempotency-Key": createAttempt.current.key },
        body,
      });
      if (createScope.current !== submittedScope) return;
      createAttempt.current = null;
      listVersion.current += 1;
      setRows((current) => [nextRequest, ...current.filter((row) => row.id !== nextRequest.id)]);
      setStatusFilter(defaultRequestStageFilterId(user.role));
      setForm(emptyRequestForm(user));
      setComposerOpen(false);
      notify(`${nextRequest.id} numaralı satın alma talebi oluşturuldu.`);
    } catch (error) {
      if (createScope.current === submittedScope) {
        notify(error instanceof Error ? error.message : "Talep oluşturulamadı.");
      }
    } finally {
      createBusy.current = false;
      setCreatingRequest(false);
    }
  };
  const openRequest = (request: PurchaseRequest) => {
    if (user.role === "SATIN_ALMA") {
      requestModalOpenerRef.current =
        document.activeElement as HTMLElement | null;
      setDetailRequestNo(request.id);
      return;
    }
    const target = procurementWorkspaceTarget(
      asProcurementRole(user.role),
      request,
    );
    if (target === "inline") {
      setExpandedRequest((current) =>
        current === request.id ? null : request.id,
      );
      return;
    }
    onOpenRequestWorkspace(request.id, target);
  };
  const openRequestDetail = async (requestNo: string) => {
    setDetailRequestNo(requestNo);
    setRequestDetail(null);
    setRequestDetailError("");
    setRequestDetailLoading(true);
    try {
      setRequestDetail(await requestWorkspaceService.load(requestNo));
    } catch (error) {
      setRequestDetailError(
        error instanceof Error
          ? error.message
          : "Talep ayrıntıları yüklenemedi.",
      );
    } finally {
      setRequestDetailLoading(false);
    }
  };
  const closeRequestDetail = () => {
    setDetailRequestNo(null);
    setRequestDetail(null);
    setRequestDetailError("");
  };
  const detailRequest = rows.find((request) => request.id === detailRequestNo);
  const detailRequestCompleted = detailRequest
    ? procurementStageOf(detailRequest) === "Tamamlandı"
    : requestDetail?.status === "Onaylandı";
  return {
    requestWorkspaceService,
    notify,
    composerOpen,
    setComposerOpen,
    user,
    onOpenRequestWorkspace,
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    departmentFilter,
    setDepartmentFilter,
    rows,
    form,
    setForm,
    planningComposerStep,
    setPlanningComposerStep,
    loading,
    loadError,
    setReloadKey,
    expandedRequest,
    detailRequestNo,
    requestDetail,
    requestDetailLoading,
    requestDetailError,
    stageOptions,
    selectedStageOption,
    departments,
    showDepartmentFilter,
    visibleRows,
    createRequest,
    creatingRequest,
    openRequest,
    openRequestDetail,
    closeRequestDetail,
    detailRequest,
    detailRequestCompleted,
  };
}
